/**
 * BCC France — PWA Install Prompt.
 *
 * Intercepts the native beforeinstallprompt event and replaces it with
 * a custom bottom banner so the user can install the app on mobile or desktop.
 *
 * Rules:
 * - Never shown when the app is already installed (browser does not fire
 *   beforeinstallprompt in that case).
 * - If the user dismisses the banner, it is not shown again for 90 days.
 * - If the user accepts, the localStorage entry is cleared so the prompt
 *   can reappear if they uninstall and revisit.
 */

const STORAGE_KEY = 'bcc_pwa_dismissed_at';
const COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

let deferredPrompt = null;

/**
 * Initialize the install prompt listener.
 * Call this once from app.js after DOMContentLoaded.
 */
export function initPwaInstall() {
  // Skip if the user dismissed the prompt recently.
  const dismissedAt = localStorage.getItem(STORAGE_KEY);
  if (dismissedAt && Date.now() - Number(dismissedAt) < COOLDOWN_MS) return;

  // beforeinstallprompt is NOT fired when the PWA is already installed.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    showBanner();
  });

  // Hide banner if the user installs via another path (e.g. browser menu).
  window.addEventListener('appinstalled', () => {
    hideBanner(false);
    localStorage.removeItem(STORAGE_KEY);
    deferredPrompt = null;
  });
}

// ---------------------------------------------------------------

function showBanner() {
  const banner = document.getElementById('pwa-install-banner');
  if (!banner) return;

  banner.hidden = false;
  // Allow layout to paint before triggering the slide-up transition.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => banner.classList.add('is-visible'));
  });

  banner.querySelector('[data-pwa-accept]')?.addEventListener('click', () => {
    hideBanner(false);
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choice) => {
        if (choice.outcome !== 'accepted') {
          // User cancelled the native dialog — treat as a dismiss.
          localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
        deferredPrompt = null;
      });
    }
  }, { once: true });

  banner.querySelector('[data-pwa-dismiss]')?.addEventListener('click', () => {
    hideBanner(true);
  }, { once: true });
}

function hideBanner(remember) {
  if (remember) {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }
  const banner = document.getElementById('pwa-install-banner');
  if (!banner) return;
  banner.classList.remove('is-visible');
  banner.addEventListener('transitionend', () => {
    banner.hidden = true;
  }, { once: true });
}
